import { useState } from 'react'
import { Link } from 'react-router'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { type QuoteAnalyticsFilters, useQuoteAnalyticsQuotes } from '@/models/quoteAnalytics'
import { EstimateDialog } from './EstimateDialog'
import { ReasonDialog } from './ReasonDialog'

const usd = (n: number) => `$${Math.round(n).toLocaleString()}`

const DECLINED_STATUSES = new Set(['DECLINED', 'UNAVAILABLE'])

export function QuotesListTab({ filters }: { filters: QuoteAnalyticsFilters }) {
  const [page, setPage] = useState(1)
  const { data, isLoading } = useQuoteAnalyticsQuotes(filters, page)
  const rows = data?.results ?? []
  const count = data?.count ?? 0
  const pageSize = 50
  const maxPage = Math.max(Math.ceil(count / pageSize), 1)

  return (
    <Card className="mt-4">
      <CardContent className="p-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Carrier</TableHead>
              <TableHead>State</TableHead>
              <TableHead>Fleet</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Premium</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={i}>
                <TableCell>{r.carrier}</TableCell>
                <TableCell>{r.state}</TableCell>
                <TableCell>{r.fleetBucket}</TableCell>
                <TableCell>{r.status}</TableCell>
                <TableCell className="text-right">{usd(r.premium)}</TableCell>
                <TableCell>{r.month}</TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-2">
                    <Link
                      className="text-indigo-600 hover:underline"
                      to={`/insure/internal/applications/${r.applicationId}`}
                    >
                      View
                    </Link>
                    {r.dotNumber && (
                      <EstimateDialog dotNumber={r.dotNumber} carrier={r.carrier} powerUnits={r.fleetSize} />
                    )}
                    {DECLINED_STATUSES.has(r.status) && (
                      <ReasonDialog reason={r.declineReason} carrier={r.carrier} />
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="flex items-center justify-between pt-3 text-sm">
          <span>
            {count} offers · page {page}/{maxPage}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" disabled={page <= 1 || isLoading} onClick={() => setPage((p) => p - 1)}>
              Prev
            </Button>
            <Button
              variant="outline"
              disabled={page >= maxPage || isLoading}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
